import React from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { getEmployees, getEmployeeBankAccounts } from "services/employees";
import { useNavigate } from "react-router-dom";

const CheckIcon = ({ done }) => (
    <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 22, height: 22, borderRadius: "50%",
        backgroundColor: done ? "#e87722" : "transparent",
        border: done ? "none" : "2px solid #666",
        flexShrink: 0,
    }}>
        {done && (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        )}
    </span>
);

const StepRow = ({ done, label, description, action, onAction }) => (
    <div className="d-flex align-items-start gap-3 py-3 border-bottom">
        <CheckIcon done={done} />
        <div className="flex-grow-1">
            <p className="m-0 sub-titles" style={{ fontWeight: 600 }}>{label}</p>
            <p className="m-0 title mt-1">{description}</p>
        </div>
        {!done && action && (
            <button className="button-orange py-1 px-3 rounded-pill" style={{ fontSize: 13, whiteSpace: "nowrap" }} onClick={onAction}>
                {action}
            </button>
        )}
        {done && <span style={{ fontSize: 12, color: "#e87722", whiteSpace: "nowrap" }}>Complete</span>}
    </div>
);

const EmployeeSetupSection = () => {
    const navigate = useNavigate();

    const { data: employees = [], isLoading, isError } = useQuery({
        queryKey: ["employees"],
        queryFn: getEmployees,
    });

    const activeEmployees = employees.filter(e => e.status === "active" || e.active);

    const bankAccountQueries = useQueries({
        queries: activeEmployees.map(e => ({
            queryKey: ["employee-bank-accounts", e.id],
            queryFn: () => getEmployeeBankAccounts(e.id),
            staleTime: 60000,
        })),
    });

    const bankAccountsLoading = bankAccountQueries.some(q => q.isLoading);
    const employeesWithAccounts = activeEmployees.filter((_, i) =>
        (bankAccountQueries[i]?.data ?? []).length > 0
    );

    const withBankAccount = employeesWithAccounts;
    const withDirectDeposit = employeesWithAccounts;

    const hasEmployees = activeEmployees.length > 0;
    const hasBankAccounts = withDirectDeposit.length > 0;
    const allHaveBankAccounts = hasEmployees && withBankAccount.length === activeEmployees.length;

    if (isLoading) return <p className="text-center mt-4">Loading employee setup...</p>;
    if (isError) return <p className="text-danger mt-4">Failed to load employee data.</p>;

    const completedSteps = [hasEmployees, hasBankAccounts, allHaveBankAccounts].filter(Boolean).length;
    const totalSteps = 3;
    const progressPct = Math.round((completedSteps / totalSteps) * 100);

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="sub-title">Employee Setup</h2>
                <button className="button-orange py-1 px-3 rounded-pill" style={{ fontSize: 13 }} onClick={() => navigate("/employees")}>
                    Manage Employees
                </button>
            </div>

            <div className="mb-4 p-3 rounded-3" style={{ backgroundColor: "rgba(232,119,34,0.08)", border: "1px solid rgba(232,119,34,0.2)" }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="title">Setup Progress</span>
                    <span className="sub-titles" style={{ fontWeight: 600 }}>{completedSteps} / {totalSteps} steps complete</span>
                </div>
                <div style={{ height: 8, backgroundColor: "rgba(255,255,255,0.1)", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ width: `${progressPct}%`, height: "100%", backgroundColor: "#e87722", borderRadius: 4, transition: "width 0.3s ease" }} />
                </div>
            </div>

            <div className="mb-4">
                <div className="d-flex gap-4">
                    <div className="text-center">
                        <p className="m-0 sub-titles" style={{ fontSize: 24, fontWeight: 700 }}>{activeEmployees.length}</p>
                        <p className="m-0 title">Active Employees</p>
                    </div>
                    <div className="text-center">
                        <p className="m-0 sub-titles" style={{ fontSize: 24, fontWeight: 700 }}>
                            {bankAccountsLoading ? "…" : withDirectDeposit.length}
                        </p>
                        <p className="m-0 title">With Direct Deposit</p>
                    </div>
                    <div className="text-center">
                        <p className="m-0 sub-titles" style={{ fontSize: 24, fontWeight: 700 }}>
                            {bankAccountsLoading ? "…" : activeEmployees.length - withDirectDeposit.length}
                        </p>
                        <p className="m-0 title">Pending Setup</p>
                    </div>
                </div>
            </div>

            <div>
                <p className="info-title mb-2">Setup Checklist</p>
                <StepRow
                    done={hasEmployees}
                    label="Add Employees"
                    description={hasEmployees ? `${activeEmployees.length} active employee${activeEmployees.length !== 1 ? "s" : ""} added` : "No employees have been added yet"}
                    action="Add Employee"
                    onAction={() => navigate("/employees/add-edit-employee")}
                />
                <StepRow
                    done={hasBankAccounts}
                    label="Set Up Direct Deposit"
                    description={hasBankAccounts ? `${withDirectDeposit.length} employee${withDirectDeposit.length !== 1 ? "s" : ""} have direct deposit configured` : "Add bank accounts to employees for direct deposit payments"}
                    action="Set Up Now"
                    onAction={() => navigate("/employees")}
                />
                <StepRow
                    done={allHaveBankAccounts}
                    label="Complete All Employee Payment Setup"
                    description={allHaveBankAccounts ? "All employees have payment methods configured" : `${activeEmployees.length - withBankAccount.length} employee${(activeEmployees.length - withBankAccount.length) !== 1 ? "s" : ""} still need a payment method`}
                    action="Review Employees"
                    onAction={() => navigate("/employees")}
                />
            </div>
        </div>
    );
};

export default EmployeeSetupSection;
